import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';

import { SagaPayload } from '../api.types';
import {
  CreateCollectionRequest,
  CreateCollectionResponse,
  DeleteCollectionRequest,
  DeleteCollectionResponse,
  UpdateCollectionRequest,
  UpdateCollectionResponse,
  ViewAllCollectionsRequest,
  ViewAllCollectionsResponse
} from './collection.types';

import {
  CREATING_COLLECTION,
  DELETING_COLLECTION,
  RETRIEVING_ALL_COLLECTIONS,
  UPDATING_COLLECTION
} from './collection.constants';

import { requestViewAllProgrammes } from '../programme/v2/programme.actions';
import {
  requestCreateCollection,
  requestDeleteCollection,
  requestUpdateCollection,
  requestViewAllCollections
} from './collection.actions';

import {
  getCreateCollectionRequest,
  getDeleteCollectionRequest,
  getUpdateCollectionRequest,
  getViewAllCollectionsRequest
} from './collection.endpoints';

import { setCollections } from '../../slices/programme/plan/plan.slice';
import { addSpinner, addSystemNotice, removeSpinner } from '../../slices/system/system.slice';

import { getErrorMessage } from '../../utils/api.util';
import { ERROR, SUCCESS } from '../../utils/constants';

export function* performCreateCollectionRequest({ payload }: SagaPayload<CreateCollectionRequest, string>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(CREATING_COLLECTION));

    const [endpoint, options] = getCreateCollectionRequest(requestData);

    const { data }: AxiosResponse<CreateCollectionResponse> = yield call(axios, endpoint, options);

    if (onSuccess && data.plans[0]) {
      yield call(onSuccess, data.plans[0].externalReference!);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(
      requestViewAllCollections({ requestData: { programmeExternalReference: requestData.programmeExternalReference } })
    );
    yield put(requestViewAllProgrammes());
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(CREATING_COLLECTION));
  }
}

export function* watchForCreateCollectionRequest() {
  yield takeLatest(requestCreateCollection.type, performCreateCollectionRequest);
}

export function* performViewAllCollectionsRequest({ payload }: SagaPayload<ViewAllCollectionsRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(RETRIEVING_ALL_COLLECTIONS));

    const [endpoint, options] = getViewAllCollectionsRequest(requestData);

    const { data }: AxiosResponse<ViewAllCollectionsResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(setCollections(data.collections));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_ALL_COLLECTIONS));
  }
}

export function* watchForViewAllCollectionsRequest() {
  yield takeLatest(requestViewAllCollections.type, performViewAllCollectionsRequest);
}

export function* performUpdateCollectionRequest({ payload }: SagaPayload<UpdateCollectionRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(UPDATING_COLLECTION));

    const [endpoint, options] = getUpdateCollectionRequest(requestData);

    const { data }: AxiosResponse<UpdateCollectionResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(
      requestViewAllCollections({
        requestData: { programmeExternalReference: requestData.collection.programmeExternalReference! }
      })
    );
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(UPDATING_COLLECTION));
  }
}

export function* watchForUpdateCollectionRequest() {
  yield takeLatest(requestUpdateCollection.type, performUpdateCollectionRequest);
}

export function* performDeleteCollectionRequest({ payload }: SagaPayload<DeleteCollectionRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(DELETING_COLLECTION));

    const [endpoint, options] = getDeleteCollectionRequest(requestData);

    const { data }: AxiosResponse<DeleteCollectionResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(
      requestViewAllCollections({ requestData: { programmeExternalReference: requestData.programmeExternalReference } })
    );
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(DELETING_COLLECTION));
  }
}

export function* watchForDeleteCollectionRequest() {
  yield takeLatest(requestDeleteCollection.type, performDeleteCollectionRequest);
}

export default function* collectionSaga() {
  yield all([
    watchForCreateCollectionRequest(),
    watchForViewAllCollectionsRequest(),
    watchForUpdateCollectionRequest(),
    watchForDeleteCollectionRequest()
  ]);
}
